import React from 'react';
import PropTypes from 'prop-types';
import { useSelector, useDispatch } from 'react-redux';

import {
    Flex,
    Menu,
    MenuItem,
    MenuPopper,
    Text,
    Tag,
    IconCheckmarkCircle,
    IconWarning,
} from '@zillow/constellation';

import {
    AppSelectors,
} from 'app/shared/selectors';

import { RMWContext } from 'app/shared/providers/RMWContext';

import stringHelper from 'app/shared/utils/stringHelper';

import PropertiesActions from 'app/shared/actions/PropertiesActions';

// utils
import { STATUS_KEY, getTagTypeByKey } from 'app/shared/utils/statusTagUtils';
import { escapeRoute } from 'app/shared/utils/escapeRoute';

// styled components
import {
    StyledFlexContainer,
    StyledPropertyRow,
    StyledPropertyPhotoContainer,
    StyledPropertyPhoto,
    StyledPropertyText,
    StyledPropertyAddress,
    StyledTagWrapper,
    StyledIconZillowLogo,
    StyledTableCell,
} from './StyledComponents';

const propTypes = {
    listing: PropTypes.object.isRequired,
    hideBoost: PropTypes.bool,
};

const defaultProps = {
    hideBoost: false,
};

const PropertyRow = ({
    listing,
    hideBoost,
}) => {
    const { paths } = React.useContext(RMWContext);
    const isScreenTabletOrDesktop = useSelector(state => AppSelectors.makeIsScreenTabletOrDesktop(state));
    const isScreenDesktop = useSelector(state => AppSelectors.makeIsScreenDesktop(state));
    const isEnterpriseCustomer = useSelector(state => state.user.isEnterpriseCustomer);
    const dispatch = useDispatch();

    const show3D = !isEnterpriseCustomer || isScreenDesktop;
    const isMinWidthNeeded = !isEnterpriseCustomer && isScreenDesktop;

    const {
        photoUrl: primaryPhotoUrl,
        status,
        address,
        aliasEncoded,
        isMultifamily,
        name,
        feedName,
        feedId,
        panels,
        productTypes,
        hasBoost,
        vrModelCount,
        additionalListingSources,
        syndicationOverviewDisplayStatus,
        activityMessage,
        isConnectedExperience,
    } = listing;

    const { code, message: statusMessage } = status;

    const {
        city,
        state,
        street,
        unit,
        zip,
    } = address;

    const fullAddress = unit ? `${street} ${unit}` : street;
    const isZillow = feedName === 'Zillow Rental Manager';

    const handleRowClick = (event) => {
        event.preventDefault();
        event.stopPropagation();

        if (isMultifamily || !isConnectedExperience) {
            window.router.transitionTo(`${paths.propertiesPage}/${aliasEncoded}/listing`);
        } else {
            dispatch(PropertiesActions.getIsConnectedExperience(aliasEncoded)).finally(() => {
                window.router.transitionTo(panels[0].urlPath);
            });
        }
    };

    const handleMenuClick = (event) => {
        event.preventDefault();
        event.stopPropagation();
    };

    const handleMenuItemClick = (item) => {
        const { urlPath: url, id } = item;

        dispatch(PropertiesActions.getIsConnectedExperience(aliasEncoded)).finally(() => {
            if (escapeRoute(id)) {
                window.router.updateLocation(url);
            } else {
                window.router.transitionTo(url);
            }
        });
    };

    const menu = (
        <Menu>
            {panels.filter(item => !item.isDisabled).map((item, index) => (
                <MenuItem
                    key={index}
                    onClick={() => handleMenuItemClick(item)}
                >
                    {item.title}
                </MenuItem>
            ))}
        </Menu>
    );

    const getStatusIcon = () => {
        const STATUS = {
            ACTIVE: 'active',
            INACTIVE: 'inactive',
            DECLINED: 'declined',
        };
        const size = 'xs';

        switch (code) {
            case STATUS.ACTIVE:
                return (
                    <IconCheckmarkCircle
                        fontColor="green400"
                        size={size}
                    />
                );
            case STATUS.DECLINED:
                return (
                    <IconWarning
                        fontColor="orange400"
                        size={size}
                    />
                );
            case STATUS.INACTIVE:
            default:
                return null;
        }
    };

    return (
        <StyledPropertyRow onClick={handleRowClick}>
            {isScreenTabletOrDesktop && (
                <StyledTableCell>
                    <StyledPropertyPhotoContainer>
                        <StyledPropertyPhoto primaryPhotoUrl={primaryPhotoUrl} />
                    </StyledPropertyPhotoContainer>
                </StyledTableCell>
            )}
            <StyledTableCell>
                <StyledFlexContainer>
                    <Flex display="flex" flexDirection="column">
                        <StyledTagWrapper>
                            <Tag
                                appearance={getTagTypeByKey(STATUS_KEY.PROPERTY_DETAILS, code)}
                                size="sm"
                            >
                                <b>{statusMessage}</b>
                            </Tag>
                        </StyledTagWrapper>
                        <StyledPropertyAddress isMultifamily={isMultifamily}>
                            <StyledPropertyText fontType="h6" as="p" isAddress={true}>
                                {isMultifamily ? name : fullAddress}
                            </StyledPropertyText>
                            <StyledPropertyText fontType="bodySmall" as="p" isAddress={true}>
                                {isMultifamily ?
                                    (
                                        <React.Fragment>
                                            {fullAddress}
                                            <br />
                                        </React.Fragment>
                                    ) : null
                                }
                                {`${city}, ${state} ${zip}`}
                            </StyledPropertyText>
                        </StyledPropertyAddress>
                    </Flex>
                </StyledFlexContainer>
            </StyledTableCell>
            {show3D && (
                <StyledTableCell isMinWidthNeeded={isMinWidthNeeded}>
                    <StyledFlexContainer>
                        <StyledPropertyText>
                            {vrModelCount > 0 ?
                                (
                                    <Text fontType="bodySmall" as="p">
                                        Added
                                        {vrModelCount > 1 && ` (${vrModelCount})`}
                                    </Text>
                                ) :
                                (
                                    <Text fontColor="gray400" fontType="bodySmall" as="p">
                                        No tour
                                    </Text>
                                )
                            }
                        </StyledPropertyText>
                    </StyledFlexContainer>
                </StyledTableCell>
            )}
            {(isEnterpriseCustomer && isScreenTabletOrDesktop) && (
                <React.Fragment>
                    <StyledTableCell>
                        <StyledFlexContainer>
                            <StyledPropertyText fontType="bodySmall" as="p">
                                <strong>
                                    {isZillow ? <StyledIconZillowLogo size="sm" /> : null}
                                    {feedName || feedId}
                                    &nbsp;
                                    {additionalListingSources.length > 0 ?
                                        `(${stringHelper.pluralize(
                                            additionalListingSources.length, 'other', 'others'
                                        )})` : ''
                                    }
                                </strong>
                                <br />
                                {activityMessage}
                            </StyledPropertyText>
                        </StyledFlexContainer>
                    </StyledTableCell>
                    <StyledTableCell>
                        <StyledFlexContainer>
                            <StyledPropertyText fontType="bodySmallHeading" as="p">
                                {productTypes.length > 0 ? productTypes.join(', ') : '-'}
                            </StyledPropertyText>
                        </StyledFlexContainer>
                    </StyledTableCell>
                </React.Fragment>
            )}
            <StyledTableCell isMinWidthNeeded={isMinWidthNeeded}>
                <StyledFlexContainer>
                    {getStatusIcon()}
                    <StyledPropertyText fontType="bodySmallHeading" as="p" marginLeft={true}>
                        {syndicationOverviewDisplayStatus}
                    </StyledPropertyText>
                </StyledFlexContainer>
            </StyledTableCell>
            {!hideBoost && (
                <StyledTableCell>
                    <StyledFlexContainer>
                        <StyledPropertyText>
                            {hasBoost ? 'Boost' : '-'}
                        </StyledPropertyText>
                    </StyledFlexContainer>
                </StyledTableCell>
            )}
            <StyledTableCell onClick={handleMenuClick}>
                <StyledFlexContainer display="flex">
                    <MenuPopper triggered={menu} />
                </StyledFlexContainer>
            </StyledTableCell>
        </StyledPropertyRow>
    );
};

PropertyRow.propTypes = propTypes;
PropertyRow.defaultProps = defaultProps;

export { PropertyRow };
export default PropertyRow;
